import { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import type { CellContextMenuEvent, ColDef, SortChangedEvent } from "ag-grid-community";

import { getWatchlistRowColor } from "@/components/watchlist/watchlistColors";
import type { WatchlistItem } from "@/services/api/types";
import { formatCompact, formatNumber } from "@/lib/utils";

export interface WatchlistSortState {
  field: string;
  direction: "asc" | "desc";
}

export interface WatchlistRowContextMenuPayload {
  event: MouseEvent;
  row: WatchlistItem;
}

interface WatchlistGridProps {
  rows: WatchlistItem[];
  activeSymbol?: string;
  defaultSort?: {
    field: string;
    direction: "asc" | "desc";
  };
  gridKey?: string;
  onSelectSymbol: (symbol: string) => void;
  onSortChange?: (sort: WatchlistSortState | null) => void;
  onRowContextMenu?: (payload: WatchlistRowContextMenuPayload) => void;
}

export function WatchlistGrid({
  rows,
  activeSymbol,
  defaultSort,
  gridKey,
  onSelectSymbol,
  onSortChange,
  onRowContextMenu,
}: WatchlistGridProps) {
  const columnDefs = useMemo<ColDef<WatchlistItem>[]>(
    () => [
      {
        field: "display_name",
        headerName: "标的",
        minWidth: 140,
        flex: 1.5,
        valueGetter: (params) => {
          const row = params.data;
          return row?.display_name || row?.stock_name || row?.name_cache || row?.symbol || "--";
        },
        sort: defaultSort?.field === "name" ? defaultSort.direction : undefined,
      },
      {
        field: "symbol",
        headerName: "代码",
        minWidth: 104,
        flex: 1,
        sort: defaultSort?.field === "symbol" ? defaultSort.direction : undefined,
      },
      {
        field: "close",
        headerName: "最新价",
        minWidth: 96,
        flex: 0.8,
        type: "numericColumn",
        sort: defaultSort?.field === "close" ? defaultSort.direction : undefined,
        valueFormatter: (params: { value: number | null }) => formatNumber(params.value),
      },
      {
        field: "pct_chg",
        headerName: "涨跌%",
        minWidth: 96,
        flex: 0.8,
        type: "numericColumn",
        sort: defaultSort?.field === "pct_chg" ? defaultSort.direction : undefined,
        valueFormatter: (params: { value: number | null }) => (params.value == null ? "--" : `${formatNumber(params.value)}%`),
        cellClass: (params: { value: number | null }) => {
          if (params.value == null) return "text-[var(--muted)]";
          return params.value >= 0 ? "text-[var(--up)]" : "text-[var(--down)]";
        },
      },
      {
        field: "amount",
        headerName: "成交额",
        minWidth: 102,
        flex: 0.9,
        type: "numericColumn",
        sort: defaultSort?.field === "amount" ? defaultSort.direction : undefined,
        valueFormatter: (params: { value: number | null }) => formatCompact(params.value),
      },
      {
        field: "vol",
        headerName: "成交量",
        minWidth: 102,
        flex: 0.9,
        type: "numericColumn",
        sort: defaultSort?.field === "vol" ? defaultSort.direction : undefined,
        valueFormatter: (params: { value: number | null }) => formatCompact(params.value),
      },
      {
        field: "trade_date",
        headerName: "更新日",
        minWidth: 110,
        flex: 0.9,
        sort: defaultSort?.field === "trade_date" ? defaultSort.direction : undefined,
        valueFormatter: (params: { value: string | null }) => formatTradeDate(params.value),
      },
    ],
    [defaultSort],
  );

  return (
    <div className="ag-theme-quartz-dark h-full min-h-0">
      <AgGridReact
        key={`watchlist-grid:${gridKey || "default"}:${defaultSort?.field || "none"}:${defaultSort?.direction || "none"}:${rows.length}`}
        theme="legacy"
        rowData={rows}
        columnDefs={columnDefs as never}
        defaultColDef={{
          sortable: true,
          resizable: true,
          suppressHeaderMenuButton: true,
        }}
        getRowId={(params) => String(params.data?.id ?? params.data?.symbol ?? "")}
        getRowClass={(params) => (params.data?.symbol === activeSymbol ? "watchlist-grid-row-active" : "")}
        getRowStyle={(params) => {
          const color = getWatchlistRowColor(params.data?.row_color);
          if (!color) {
            return undefined;
          }
          return {
            backgroundColor: color.background,
            color: color.text,
          };
        }}
        suppressCellFocus
        onSortChanged={(event) => handleSortChanged(event, onSortChange)}
        onCellContextMenu={(event) => handleContextMenu(event, onRowContextMenu)}
        suppressContextMenu
        preventDefaultOnContextMenu
        rowSelection={{
          mode: "singleRow",
          checkboxes: false,
          enableClickSelection: true,
          hideDisabledCheckboxes: true,
        }}
        overlayNoRowsTemplate={'<span class="text-xs text-[var(--muted)]">当前 watchlist 为空</span>'}
        onRowClicked={(event) => onSelectSymbol(String(event.data?.symbol || ""))}
      />
    </div>
  );
}

function handleSortChanged(
  event: SortChangedEvent<WatchlistItem>,
  onSortChange?: (sort: WatchlistSortState | null) => void,
) {
  if (!onSortChange) {
    return;
  }
  const sorted = event.api
    .getColumnState()
    .find((column) => Boolean(column.sort) && Boolean(column.colId));
  if (!sorted?.colId || !sorted.sort) {
    onSortChange(null);
    return;
  }
  onSortChange({
    field: normalizeGridSortField(sorted.colId),
    direction: sorted.sort === "desc" ? "desc" : "asc",
  });
}

function handleContextMenu(
  event: CellContextMenuEvent<WatchlistItem>,
  onRowContextMenu?: (payload: WatchlistRowContextMenuPayload) => void,
) {
  if (!onRowContextMenu || !event.data) {
    return;
  }
  event.event?.preventDefault?.();
  onRowContextMenu({
    event: event.event as unknown as MouseEvent,
    row: event.data,
  });
}

function normalizeGridSortField(field: string) {
  if (field === "display_name" || field === "name_cache") {
    return "name";
  }
  if (field === "volume") {
    return "vol";
  }
  if (field === "turnover") {
    return "amount";
  }
  return field;
}

function formatTradeDate(value: string | null | undefined) {
  const raw = String(value || "");
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw || "--";
}
